import { Component, namespace, Vue, Watch } from 'nuxt-property-decorator';
import { MetaPropertyName } from 'vue-meta';
import { IProduct, ProductsLoadMode } from '../entities/product-entity';
import { ICategory } from '../entities/category-entity';
import {
	namespace as productsStore,
	actionType as productsActionType,
	MutationType,
	IGetProductsParams,
	SortOptionsList,
} from '../store/modules/product-store';
import { namespace as brandsStore } from '../store/modules/brands-store';
import { namespace as appStore } from "../store/modules/app-store";
import {
	namespace as categoryFiltersStore,
	IGetFiltersParams,
	IUpdateFiltersParams, IGetFilterPageParams,
} from '../store/modules/category-filters-store';
import { namespace as wishlistStore, actionType as wishlistActionType } from '../store/modules/wishlist-store';

import { IFilter, IFilterVariant, IQueryFilter } from '../entities/filter-entity';
import { IWishlistItem } from '../entities/wishlist-item-entity';
import {IGetCategoryDetailedParams} from "../../lib/store/modules/catalog-store";
import CategoryMixin from "../../lib/mixins/CategoryMixin";

const AppStore = namespace(appStore);
const BrandsStore = namespace(brandsStore);
const ProductsStore = namespace(productsStore);
const CategoryFiltersStore = namespace(categoryFiltersStore);
const WishlistStore = namespace(wishlistStore);

const mixinName = "brands";

type SelectedFilters = Record<string, Record<string | number, string>>;

interface IPageHead {
	title: string;
	meta: MetaPropertyName[];
}

interface IRouteParams {
	slug: string;
	page: number;
	path: string;
	sort: string;
	perPage: number;
	filters: string | null;
}

@Component({
	head(this: BrandsMixin) {
		return this.getPageHead();
	},
	async fetch(this: BrandsMixin): Promise<void> {
		await this.loadCategoryDetailed();
		await this.loadCategory(false);

		const currentRoute = (this.$route?.path || "").split("/f/")?.[0];
		const categoryUrl = this.category?.url || "";
		if (currentRoute !== categoryUrl) {
			this.$nuxt.context.redirect(categoryUrl);
		}
	},
	mounted: async function (this: BrandsMixin) {
		await this.loadFilter()
	},
})
export default class BrandsMixin extends Vue {
	@BrandsStore.Action('GET_CATEGORY_DETAILED')
	readonly getCategoryDetailed!: (params: IGetCategoryDetailedParams) => void;

	@BrandsStore.Getter('brand')
	readonly category!: ICategory;

	@AppStore.Getter('config')
	readonly appCfg!: any;

	@ProductsStore.Action(productsActionType.GET_PRODUCTS)
	readonly getProducts!: (params: IGetProductsParams) => void;

	@ProductsStore.Action(productsActionType.RESET_STATE)
	readonly resetProductState!: () => void;

	@ProductsStore.Mutation(MutationType.SET_LOAD_MODE)
	readonly setProductsLoadMode!: (mode: ProductsLoadMode) => void;

	@ProductsStore.Getter('products')
	readonly products!: IProduct[];

	@ProductsStore.Getter('sort')
	readonly categorySort!: string;

	@ProductsStore.Getter('total')
	readonly total!: number;

	@ProductsStore.Getter('pageCount')
	readonly pageCount!: number;

	@ProductsStore.Getter('pagination')
	readonly pagination!: { page: number; perPage: number };

	@CategoryFiltersStore.Action('updateFilters')
	readonly updateFilters!: (params: IUpdateFiltersParams) => Promise<IFilter[]>;

	@CategoryFiltersStore.Action('getFilters')
	readonly getFilters!: (params: IGetFiltersParams) => Promise<IFilter[]>;

	@CategoryFiltersStore.Getter('filters')
	readonly filters!: IFilter[];

	@CategoryFiltersStore.Getter('filterPages')
	readonly filterPages!: any[];

	@CategoryFiltersStore.Getter('filterPrices')
	readonly filterPrices!: any;

	// TODO Тут надо добаить параметр для mixinName
	@CategoryFiltersStore.Action('getFilterPages')
	readonly getFilterPages!: (params: IGetFilterPageParams) => Promise<any>;

	@WishlistStore.Action(wishlistActionType.ADD_TO_WISHLIST)
	readonly addItemToWishlist!: (id: number) => Promise<IWishlistItem>;

	@WishlistStore.Action(wishlistActionType.REMOVE_ITEM_FROM_WISHLIST)
	readonly removeItemFromWishlist!: (id: number) => Promise<IWishlistItem>;

	@WishlistStore.Getter
	readonly isInWishlist!: (id: number) => boolean;

	selectedFilters: SelectedFilters = {};
	_routerRoot: any;

	get sortOptions(): SortOptionsList {
		return [
			{
				id: 'default',
				type: 'string',
				value: 'По умолчанию',
			},
			{
				id: 'price',
				type: 'string',
				value: 'Стоимость (Дешевле)',
			},
			{
				id: '-price',
				type: 'string',
				value: 'Стоимость (Дорогие)',
			},
			{
				id: '-created_at',
				type: 'string',
				value: 'Сначала новые',
			},
		];
	}

	get extensions(): string[] {
		return [];
	}
	get extensionsCategory(): string[] {
		return [];
	}
	get canceledOptions(): object {
		return {}
	}
	get customSlug (): string {
		return ""
	}

	get categoryFilters(): IFilter[] {
		return (this.filters || []).map((filter) => {
			const isActive = filter.slug in this.selectedFilters;
			let { variants } = filter;

			if (isActive) {
				variants = filter.variants.map(
					(variant): IFilterVariant => ({
						...variant,
						isActive: variant.slug in this.selectedFilters[filter.slug],
					}),
				);
			}

			return {
				...filter,
				variants,
				isActive,
			};
		});
	}

	get routerParams(): IRouteParams {
		const { query, path } = this.$route;
		const [routePath, routeFilters] = path.split('/f/');
		const [slug] = (routePath as string).split('/').reverse();

		const routeParams: IRouteParams = {
			slug: this.customSlug || slug,
			page: 1,
			sort: '',
			path: routePath,
			filters: routeFilters,
			perPage: 24,
		};

		if (query.page) {
			routeParams.page = Number(query.page);
		}
		if (query.sort) {
			routeParams.sort = query.sort as string;
		}

		if (query['per-page']) {
			routeParams.perPage = Number(query['per-page']);
		}

		return routeParams;
	}

	@Watch('$route')
	onRouteChanged(): void {
		this.loadFilterPages();
		this.loadCategoryDetailed();
		this.loadCategory(true);
	}

	destroyed(): void {
		this.resetProductState();
	}

	onChangeFilterVariant(filterSlug: string, variantSlug: string): void {
		const selectedFilters: SelectedFilters = JSON.parse(JSON.stringify(this.selectedFilters));
		if (filterSlug in selectedFilters) {
			if (variantSlug in selectedFilters[filterSlug]) {
				delete selectedFilters[filterSlug][variantSlug];
				if (Object.keys(selectedFilters[filterSlug]).length === 0) {
					delete selectedFilters[filterSlug];
				}
			} else {
				selectedFilters[filterSlug][variantSlug] = variantSlug;
			}
		} else {
			selectedFilters[filterSlug] = {
				[variantSlug]: variantSlug,
			};
		}

		this.selectedFilters = selectedFilters;
		this.updateFiltersQuery();
	}

	// Обновление фильтров
	async getUpdateFilters (filter: any): Promise <void> {
		const { slug, filters } = this.routerParams;

		// Сбор фильтр для бекенда
		const updateFiltersRequest: IQueryFilter[] = [];
		Object.keys(filter || {}).map((variantSlug) => {
			let values: any = [];

			Object.keys(filter[variantSlug]).map((value) => {
				values.push(value);
			});

			updateFiltersRequest.push({
				name: variantSlug,
				values: values
			})
		});

		// Получение активных фильтров
		await this.getFilters({
			slug,
			slugs: [],
			filters: updateFiltersRequest,

			mixin: mixinName,
			isHideLoad: true,
			canceledOptions: this.canceledOptions
		});
	}

	// Получение товаров с передачей фильтров
	async searchProducts (filter: any): Promise <void> {
		this.selectedFilters = filter;
		this.updateFiltersQuery();
	}

	// Изменение фильтра стоимости
	onChangeFilterVariantPrice(filters: any): void {
		const selectedFilters: SelectedFilters = JSON.parse(JSON.stringify(this.selectedFilters));

		filters.map((item: any) => {
			const { filterSlug, variantSlug } = item;

			selectedFilters[filterSlug] = {
				[variantSlug]: variantSlug,
			};
		})

		this.selectedFilters = selectedFilters;
		this.updateFiltersQuery();
	}
	onRemoveFilterVariantPrice(): void {
		const selectedFilters: SelectedFilters = JSON.parse(JSON.stringify(this.selectedFilters));

		delete selectedFilters.priceFrom;
		delete selectedFilters.priceTo;

		this.selectedFilters = selectedFilters;
		this.updateFiltersQuery();
	}

	onResetFilterSlug(filterSlug: string): void {
		const selectedFilters: SelectedFilters = JSON.parse(JSON.stringify(this.selectedFilters));
		delete selectedFilters[filterSlug];

		this.selectedFilters = selectedFilters;
		this.updateFiltersQuery();
	}

	filtersToString(): string {
		const filters: string[] = [];
		Object.keys(this.selectedFilters).forEach((filterSlug) => {
			const filter = (this.filters || []).find((f) => f.slug === filterSlug);
			if (filter) {
				const variants = filter.variants
					.filter((v) => v.slug in this.selectedFilters[filterSlug])
					.map((v) => String(v.label).toLowerCase())
					.join(',');
				filters.push(`${filter.name.toLowerCase()}: ${variants}`);
			}
		});

		return filters.join('; ');
	}
	filtersToStringCustom(): any {
		const filters: string[] = [];

		let isHidePrice = false;

		Object.keys(this.selectedFilters).forEach((filterSlug) => {
			const filter = (this.filters || []).find((f) => f.slug === filterSlug);
			const selectedFilter = this.selectedFilters[filterSlug];

			if (filter) {
				const variants = filter.variants
					.filter((v) => v.slug in this.selectedFilters[filterSlug])
					.map((v) => String(v.label).toLowerCase())
					.join(',');

				if (Object.keys(selectedFilter).length === 1) {
					filters.push(`${filter.name.toLowerCase()} ${variants}`);
				}
				if (!isHidePrice && Object.keys(selectedFilter).length > 1) {
					isHidePrice = true;
				}
			}
		});

		return {
			string: filters.join(', '),
			isHidePrice: true
		};
	}

	clearFilters(): void {
		const { path } = this.routerParams;
		this.selectedFilters = {};
		this.$router.push({ path });
	}

	updatePageQuery({ page, perPage, sort }: { page?: number; perPage?: number; sort?: string }): void {
		const routeQuery = this.routerParams;

		const query: Record<string, string> = {};

		const pagePerPage = Number(perPage || routeQuery.perPage);
		const pageNum = Number(page || routeQuery.page);
		const pageSort = sort || routeQuery.sort;

		if (pageNum && pageNum !== 1) {
			query.page = String(pageNum);
		}
		if (pagePerPage && pagePerPage > 24) {
			query['per-page'] = String(pagePerPage);
		}
		if (pageSort) {
			query.sort = sort || routeQuery.sort;
		}

		this.$router.push({ query });
	}

	onChangePage(page: number): void {
		this.setProductsLoadMode('page');
		this.updatePageQuery({ page });
	}

	onChangeSort(sort: string): void {
		this.setProductsLoadMode('page');
		this.updatePageQuery({ sort });
	}

	loadMoreProducts(): void {
		if (this.pagination.page + 1 <= +this.pageCount) {
			this.setProductsLoadMode('more');
			this.updatePageQuery({ page: this.pagination.page + 1 });
		}
	}

	onChangePerPage(perPage: number): void {
		this.updatePageQuery({ perPage });
	}


	private async loadCategory(isRouteChanged: boolean): Promise<void> {
		const { slug, page, perPage, sort, filters } = this.routerParams;
		const selectedFilters: SelectedFilters = {};
		const requestFilters: string[] = [];
		const filtersServer = this.filters;

		if (filters) {
			const filtersSplit = (filters || "").split("/");
			filtersSplit.map((selectedFilterString) => {
				const [selectedFilterSlug, selectedFilterVariants] = (selectedFilterString || "").split("=");
				selectedFilters[selectedFilterSlug] = {};
				const filterServer = (filtersServer || []).find((t) => t.slug === selectedFilterSlug);
				const filterServerTemplate = filterServer?.filterTemplate || "";

				(selectedFilterVariants || "").split(",").map((variant) => {
					variant = variant.trim();
					selectedFilters[selectedFilterSlug][variant] = variant;

					if (filterServerTemplate === "checkbox_list_with_range") {
						const [rangeFrom, rangeTo] = (variant || "").split(":");
						requestFilters.push(`filter[${selectedFilterSlug}]=${variant}`);
					}
					else if (selectedFilterSlug === "priceFrom" || selectedFilterSlug === "priceTo") {
						requestFilters.push(`${selectedFilterSlug}=${variant}`);
					}
					else {
						requestFilters.push(`filter[${selectedFilterSlug}][]=${variant}`);
					}
				})
			})
		}

		this.selectedFilters = this._sortedSelectedFiltes(selectedFilters);

		const requestParams: IGetProductsParams = {
			slug,
			page,
			filters: requestFilters,
			extensions: [],
			mixin: mixinName
		};
		if (perPage) {
			requestParams.perPage = perPage;
		}
		if (sort) {
			requestParams.sort = sort;
		}

		if ((this.category && this.category.slug !== slug) || !this.category) {
			const category = await this.loadCategoryDetailed();

			if (category === undefined) {
				this.$nuxt.error({ statusCode: 404, message: `Category ${slug} not found` });
			}
		}

		await this.loadFilterPages();

		requestParams.extensions = this.extensions;

		await this.getProducts(requestParams);
	}
	private async loadCategoryDetailed(): Promise<void> {
		const { slug } = this.routerParams;

		const requestParams: IGetCategoryDetailedParams = { slug, extensions: [] };
		requestParams.extensions = this.extensionsCategory;

		await this.getCategoryDetailed(requestParams)
	}
	private async loadFilterPages (): Promise<void> {
		let { path, filters } = this.routerParams;

		if (!filters) {
			return
		}
		if (!!filters) {
			path = `${ path }/f/${ filters }`;
		}

		const filterPages = await this.getFilterPages({
			path,
			mixin: mixinName
		});
		if (!!filterPages?.redirect) {
			if (process.client) {
				this.$router.replace(filterPages?.redirect);
			} else {
				this._routerRoot.context.redirect(filterPages?.redirect);
			}
		}
	}
	private async loadFilter(): Promise<void> {
		const { slug, page, perPage, sort, filters } = this.routerParams;

		const selectedFilters: SelectedFilters = {};
		const requestFilters: string[] = [];
		if (filters) {
			filters.split('/').forEach((filter) => {
				const [s, variants] = filter.split('=');
				selectedFilters[s] = {};
				variants.split(',').forEach((variant) => {
					const v = variant.trim();
					selectedFilters[s][v] = v;

					// Для фильтра по стоимости нужно по другому отправлять в бекенд
					if (
						s === 'priceFrom' ||
						s === 'priceTo'
					) {
						requestFilters.push(`${s}=${v}`);
					} else {
						requestFilters.push(`filter[${s}][]=${v}`);
					}
				});
			});
		}

		// Сбор фильтр для бекенда
		const updateFiltersRequest: IQueryFilter[] = [];
		Object.keys(selectedFilters || {}).map((variantSlug) => {
			let values: any = [];

			Object.keys(selectedFilters[variantSlug]).map((value) => {
				values.push(value);
			});

			updateFiltersRequest.push({
				name: variantSlug,
				values: values
			})
		});

		await this.getFilters({
			slug,
			slugs: [],
			filters: updateFiltersRequest,

			mixin: mixinName,
			isHideLoad: true,
			canceledOptions: this.canceledOptions
		});

		// const isValidDecencyFiltersQuery = this.checkValidDecencyFiltersQuery(selectedFilters);
		// if (!isValidDecencyFiltersQuery) {
		// 	this.replaceFiltersQuery();
		// }
	}

	updateFiltersQuery(): void {
		const routeQuery: string[] = [];

		(this.filters || []).map(( filtrer ) => {
			if (Boolean(this.selectedFilters[filtrer.slug])) {
				routeQuery.push(`${filtrer.slug}=${Object.keys(this.selectedFilters[filtrer.slug]).join(',')}`);
			}
		});
		if (this.selectedFilters.priceFrom) {
			routeQuery.push(`priceFrom=${Object.keys(this.selectedFilters.priceFrom || {})?.[0]}`);
		}
		if (this.selectedFilters.priceTo) {
			routeQuery.push(`priceTo=${Object.keys(this.selectedFilters.priceTo || {})?.[0]}`);
		}

		const query = routeQuery.join('/');
		this.$router.push(`${this.routerParams.path}${query ? `/f/${query}` : ''}`);
	}
	replaceFiltersQuery(): void {
		const routeQuery: string[] = [];

		(this.filters || []).map(( filtrer ) => {
			if (Boolean(this.selectedFilters[filtrer.slug])) {
				routeQuery.push(`${filtrer.slug}=${Object.keys(this.selectedFilters[filtrer.slug]).join(',')}`);
			}
		});
		if (this.selectedFilters.priceFrom) {
			routeQuery.push(`priceFrom=${Object.keys(this.selectedFilters.priceFrom || {})?.[0]}`);
		}
		if (this.selectedFilters.priceTo) {
			routeQuery.push(`priceTo=${Object.keys(this.selectedFilters.priceTo || {})?.[0]}`);
		}

		const query = routeQuery.join('/');
		this.$router.replace(`${this.routerParams.path}${query ? `/f/${query}` : ''}`);
	}
	checkValidDecencyFiltersQuery (selectedFilters: any) {
		let validFiltersQuery = [];
		(this.filters || []).map(( filtrer ) => {
			if (Boolean(selectedFilters[filtrer.slug])) {
				validFiltersQuery.push(`${filtrer.slug}=${Object.keys(selectedFilters[filtrer.slug]).join(',')}`);
			}
		});
		if (selectedFilters.priceFrom) {
			validFiltersQuery.push(`priceFrom=${Object.keys(selectedFilters.priceFrom || {})?.[0]}`);
		}
		if (selectedFilters.priceTo) {
			validFiltersQuery.push(`priceTo=${Object.keys(selectedFilters.priceTo || {})?.[0]}`);
		}
		const stringValidFiltersQuery = validFiltersQuery.join('/');
		const validPath = `${this.routerParams.path}${stringValidFiltersQuery ? `/f/${stringValidFiltersQuery}` : ''}`;

		let currentFiltersQuery: any = [];
		Object.keys(selectedFilters).forEach((filter: string) => {
			currentFiltersQuery.push(`${filter}=${Object.keys(selectedFilters[filter]).join(',')}`);
		});
		const stringCurrentFiltersQuery = currentFiltersQuery.join('/');
		const currentPath = `${this.routerParams.path}${stringCurrentFiltersQuery ? `/f/${stringCurrentFiltersQuery}` : ''}`;

		return Boolean(validPath === currentPath)
	}

	getPageHead(): IPageHead {
		const head: IPageHead = {
			title: '',
			meta: [],
		};
		// if (this.promotion) {
		// 	const { metaKeywords, metaTitle, metaDescription } = this.promotion;
		// 	const filtersMeta = this.filtersToString();
		//
		// 	let title = metaTitle;
		// 	if (filtersMeta) {
		// 		title += ` ${filtersMeta}`;
		// 	}
		//
		// 	head.title = title;
		//
		// 	if (metaDescription) {
		// 		head.meta.push({ name: 'description', content: metaDescription });
		// 	}
		// 	if (metaKeywords) {
		// 		head.meta.push({ name: 'keywords', content: metaKeywords });
		// 	}
		// }
		return head;
	}
	getPageHeadCustom ({ title: initTitle, description: initDescription, keywords: initKeywords }: any) {
		const head: IPageHead = {
			title: '',
			meta: [],
		};

		// Если пустой FILTER PAGES или Если не выбранно ни одного фильтра
		const isEmptySelectedFilter = Boolean(Object.keys(this.selectedFilters || {}).length <= 0);
		const isFillFilterPages = Boolean((this.filterPages || []).length > 0);
		if (isEmptySelectedFilter || isFillFilterPages) {
			return {
				title: initTitle,
				meta: [
					{ hid: "metadescription", name: 'description', content: initDescription },
					{ name: 'keywords', content: initKeywords },
				]
			}
		}

		const {
			string: filtersMeta,
			isHidePrice
		} = this.filtersToStringCustom();
		const city = this.appCfg?.city?.data?.prepositional || this.appCfg?.city?.model?.name || "";

		let title = initTitle;
		if (title) {
			title = `Купить ${ this.category?.name } ${filtersMeta} в ${city}`;
			if (!isHidePrice) {
				title = `${title} по цене от ${this.filterPrices?.minPrice} рублей`
			}
		}
		head.title = title;

		let description = initDescription;
		if (description) {
			const price = isHidePrice ? "" : ` по цене от ${ this.filterPrices?.minPrice } рублей`;
			description = `Покупайте ${ this.category?.name } ${ filtersMeta } в ${city}${price}. Большой выбор ${this.category?.name} в магазинах сети “Очки для Вас” с доставкой`
			head.meta.push({ hid: "metadescription", name: 'description', content: description });
		}

		let keywords = initKeywords;
		if (keywords) {
			head.meta.push({ name: 'keywords', content: keywords });
		}

		return head
	}

	isDisabledFilterVariant(filter: IFilter, id: number): boolean {
		return !filter.available.includes(id);
	}

	getFilterToString (filter: any): Promise<void> {
		filter = this._sortedSelectedFiltes(filter);

		let filterString: any = [];
		Object.keys(filter).map((key) => {
			Object.keys(filter[key]).map((v) => {
				if (key === 'priceFrom' || key === 'priceTo') {
					filterString.push(`${key}=${v}`);
				} else {
					filterString.push(`filter[${key}][]=${v}`);
				}
			})
		})

		return filterString.join("&")
	}

	// Вспомогательный фунции
	_sortedSelectedFiltes (filter: SelectedFilters) {
		let filterCharacteristics: any = {};
		let filterCategories: any = {};
		let filterPrices: any = {};

		const functionSorted = function (object: any) {
			return Object
				.keys(object)
				.sort()
				.reduce(
					(obj: any, key: any) => {
						obj[key] = object[key];
						return obj;
					},
					{}
				)
		}

		Object.keys(filter).map((key) => {
			if (key === "categories") {
				filterCategories[key] = filter[key];
			}
			else if (key === "priceFrom" || key === "priceTo") {
				filterPrices[key] = filter[key];
			}
			else {
				filterCharacteristics[key] = filter[key];
			}
		})

		Object.keys(filterCharacteristics).map((key: string) => {
			filterCharacteristics[key] = functionSorted(filterCharacteristics[key]);
		});
		Object.keys(filterCategories).map((key: string) => {
			filterCategories[key] = functionSorted(filterCategories[key]);
		});
		Object.keys(filterPrices).map((key: string) => {
			filterPrices[key] = functionSorted(filterPrices[key]);
		});

		filterCharacteristics = functionSorted(filterCharacteristics);
		filterCategories = functionSorted(filterCategories);
		filterPrices = functionSorted(filterPrices);

		return {
			...filterCharacteristics,
			...filterCategories,
			...filterPrices
		}
	}
	_sortedSelectedFiltesString (filters: string) {
		let selectedFilters: SelectedFilters = {};
		if (filters) {
			filters.split('/').forEach((filter) => {
				const [s, variants] = filter.split('=');
				selectedFilters[s] = {};
				variants.split(',').forEach((variant) => {
					const v = variant.trim();
					selectedFilters[s][v] = v;
				});
			});
		}

		selectedFilters = this._sortedSelectedFiltes(selectedFilters);

		let updateFiltersRequest: any = [];
		Object.keys(selectedFilters || {}).map((variantSlug) => {
			let values: any = [];

			Object.keys(selectedFilters[variantSlug]).map((value) => {
				values.push(value);
			});

			updateFiltersRequest.push(`${ variantSlug }=${ values.join(',') }`)
		});

		return updateFiltersRequest.join('/')
	}
}
